import styled from '@emotion/styled';
import { colors, Edit, Text, Tooltip } from '@innovationdepartment/proxima-ui';
import { Link } from 'react-router';
import type { BrandOverviewProps } from 'types/brandOverview';
import ContentSection, { ContentSectionTitle, ContentSectionWrapper } from 'ui/ContentSection';
import BrandWarningSection from '../../../BrandWarnings/BrandWarningSection.Container';
import PageContentWrapper from '../PageContentWrapper.View';
import AudienceUsage from './AudienceUsage';
import BrandActions from './BrandActions';
import BrandInfo from './BrandInfo/BrandInfo.View';
import BrandMetrics from './BrandMetrics';
import BrandPlan from './BrandOverviewPlan';
import BrandProfile from './BrandProfile';
import BrandTags from './BrandTags';
import DataIngestionView from './DataIngestion';
import MasterTokenAdAccount from './MasterTokenAdAccounts';

const UnstyledLink = styled(Link)`
  text-decoration: none;
  color: inherit;
`;

const TooltipText = () => (
  <div style={{ padding: '15px 15px', width: 'center' }}>
    <Text variant="body3">
      Map the system user access token to your brand to enable automated interactions with Meta’s
      APIs. This allows your app to perform tasks seamlessly without requiring manual
      authentication.
    </Text>
  </div>
);

const ContentSectionToolTitleWrapper = styled.div`
  border-bottom: 1px solid ${colors.neutral10};
  padding-bottom: 14px;
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 4px;
`;

const BrandSectionView: React.FC<
  Omit<
    BrandOverviewProps,
    'fetchBrandWarnings' | 'pageNumberBrandWarnings' | 'paginationMapBrandWarnings'
  >
> = props => {
  const { onEditBrandCategory, onEditDataIngestionPermissions, brandWarnings, billing, brand } =
    props;

  return (
    <PageContentWrapper title="Brand">
      <ContentSectionWrapper>
        {/* left column */}
        <ContentSectionWrapper column maxWidth={500}>
          <ContentSection>
            <ContentSectionTitle title="Brand info" />
            <BrandInfo {...props} />
            <ContentSectionTitle
              title="Brand profile"
              buttonProps={{
                label: <Text color="neutral100">Edit</Text>,
                startIcon: <Edit />,
                onClick: onEditBrandCategory
              }}
            />
            <BrandProfile {...props} />
            {/* this is to remove section as some brands in develop dont have billing (need to run migration for it) */}
            {!!billing && (
              <>
                <ContentSectionTitle title="Plan" />
                <BrandPlan {...props} />
              </>
            )}
            {!brand.isManaged && (
              <>
                <ContentSectionTitle title="Actions" />
                <BrandActions {...props} />
              </>
            )}
          </ContentSection>
          <ContentSection>
            <ContentSectionTitle title="Brand Tags" />
            <BrandTags />
          </ContentSection>
          <ContentSection>
            <ContentSectionTitle
              title="Data ingestion permissions"
              buttonProps={{
                label: <Text color="neutral100">Edit</Text>,
                startIcon: <Edit />,
                onClick: onEditDataIngestionPermissions
              }}
            />
            <DataIngestionView {...props} />
          </ContentSection>
        </ContentSectionWrapper>
        {/* right column */}
        <ContentSectionWrapper column>
          <ContentSection>
            <ContentSectionTitle title="Brand metrics" />
            <BrandMetrics {...props} />
          </ContentSection>
          {brandWarnings && brandWarnings.length > 0 && (
            <ContentSection>
              <UnstyledLink to={`/brand/${brand.brandId}/warnings`}>
                <ContentSectionTitle title="Warnings" />
                <BrandWarningSection brandWarnings={brandWarnings} />
              </UnstyledLink>
            </ContentSection>
          )}
          <ContentSection>
            <ContentSectionTitle title="Audience usage" />
            <AudienceUsage {...props} />
          </ContentSection>
          <ContentSection>
            <ContentSectionToolTitleWrapper>
              <Text variant="body1Semibold">Map Meta System user to Brand</Text>
              <Tooltip title={<TooltipText />} placement="left-start" arrow info />
            </ContentSectionToolTitleWrapper>
            <MasterTokenAdAccount {...props} />
          </ContentSection>
        </ContentSectionWrapper>
      </ContentSectionWrapper>
    </PageContentWrapper>
  );
};

export default BrandSectionView;
